import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { PersistConfig } from "redux-persist";
import storage from "redux-persist/es/storage";

type ColorScheme = "light" | "dark";
export type ColorSchemePreference = "light" | "dark" | "system";
type RoleTheme = "blue" | "green";

interface GeneralState {
	colorScheme: ColorScheme;
	/** User-facing theme preference. "system" follows prefers-color-scheme. */
	colorSchemePreference: ColorSchemePreference;
	/** `null` represents the "All Schools" selection. */
	selectedSchoolId: string | null;
	/**
	 * Transient override for the role-based theme. Set by auth screens that
	 * want the full UI to preview a target role (e.g. login mode toggle).
	 * When `null`, the theme follows the authenticated role.
	 */
	themePreview: RoleTheme | null;
}

const getSystemColorScheme = (): ColorScheme => {
	if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
	return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const initialState: GeneralState = {
	colorScheme: getSystemColorScheme(),
	colorSchemePreference: "system",
	selectedSchoolId: null,
	themePreview: null,
};

export const generalSlice = createSlice({
	name: "general",
	initialState,
	reducers: {
		setColorScheme: (state, action: PayloadAction<ColorScheme>) => {
			state.colorScheme = action.payload;
		},
		toggleColorScheme: (state) => {
			state.colorScheme = state.colorScheme === "light" ? "dark" : "light";
		},
		setColorSchemePreference: (state, action: PayloadAction<ColorSchemePreference>) => {
			state.colorSchemePreference = action.payload;
			if (action.payload === "light" || action.payload === "dark") {
				state.colorScheme = action.payload;
			} else {
				state.colorScheme = getSystemColorScheme();
			}
		},
		setSelectedSchoolId: (state, action: PayloadAction<string | null>) => {
			state.selectedSchoolId = action.payload;
		},
		setThemePreview: (state, action: PayloadAction<RoleTheme | null>) => {
			state.themePreview = action.payload;
		},
	},
});

export const generalPersistConfig: PersistConfig<GeneralState> = {
	key: generalSlice.name,
	storage,
	whitelist: ["selectedSchoolId", "colorSchemePreference"],
};

export const { setColorScheme, toggleColorScheme, setColorSchemePreference, setSelectedSchoolId, setThemePreview } = generalSlice.actions;
